import {z} from 'zod';

export const nonEmptyString = () =>
  z.string().min(1, {message: ZodErrors.required});

export function mapNullToUndefined<T extends z.ZodTypeAny>(schema: T) {
  return z.effect(schema, {
    type: 'preprocess',
    transform: (value) => value ?? undefined,
  });
}

export function mapStringNumberToNumber<T extends z.ZodTypeAny>(schema: T) {
  return z.effect(schema, {
    type: 'preprocess',
    transform: (value) => {
      if (typeof value === 'string') {
        return Number(value);
      }
      return value;
    },
  });
}

export const ZodErrors = {
  required: 'Required',
} as const;

export const isRequired = <T extends z.AnyZodObject, K extends string>(
  schema: T,
  propertyPath: K,
) => {
  const segments = propertyPath
    .split('.')
    .filter((segment) => isNaN(Number(segment)));
  let currentSchema: any = schema;

  segments.forEach((segment) => {
    if (currentSchema instanceof z.ZodOptional) {
      currentSchema = currentSchema.unwrap();
    }

    if (currentSchema instanceof z.ZodArray) {
      currentSchema = currentSchema.element;
    }

    if (currentSchema instanceof z.ZodObject) {
      currentSchema = currentSchema.shape[segment];
    }
  });

  return !currentSchema.isOptional() && !currentSchema.isNullable();
};

export function mapStringToBoolean<T extends z.ZodTypeAny>(schema: T) {
  return z.effect(schema, {
    type: 'preprocess',
    transform: (value) => {
      if (typeof value === 'string') {
        if (value.toLowerCase() === 'true') return true;
        if (value.toLowerCase() === 'false') return false;
      }
      return value;
    },
  });
}

/**
 * Generates default values for a given Zod schema.
 * @param schema - The Zod schema for which to generate default values.
 * @returns The default values based on the schema type.
 */
export const getDefaults = <T extends z.ZodTypeAny>(schema: T): z.infer<T> => {
  const {typeName} = schema._def;
  switch (typeName) {
    case z.ZodFirstPartyTypeKind.ZodString:
      return '' as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodNumber:
      return 0 as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodBoolean:
      return false as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodDate:
      return new Date() as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodArray:
      return [] as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodObject:
      const shape = (schema as unknown as z.ZodObject<any>).shape;
      const defaults: Record<string, any> = {};
      for (const key in shape) {
        defaults[key] = getDefaults(shape[key]);
      }
      return defaults as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodOptional:
      return undefined as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodNullable:
      return null as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodUnion:
      const options = (
        schema as unknown as z.ZodUnion<[z.ZodTypeAny, ...z.ZodTypeAny[]]>
      )._def.options;
      return getDefaults(options[0]) as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodDiscriminatedUnion:
      const discrimOptions = (
        schema as unknown as z.ZodDiscriminatedUnion<any, any>
      )._def.options;
      return getDefaults(discrimOptions.values().next().value) as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodTuple:
      const items = (schema as unknown as z.ZodTuple).items;
      return items.map((item) => getDefaults(item)) as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodRecord:
      return {} as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodMap:
      return new Map() as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodSet:
      return new Set() as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodNativeEnum:
      const enumValues = Object.values(
        (schema as unknown as z.ZodNativeEnum<any>)._def.values,
      );
      return enumValues[0] as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodDefault:
      return (
        schema as unknown as z.ZodDefault<z.ZodTypeAny>
      )._def.defaultValue() as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodLiteral:
      return (schema as unknown as z.ZodLiteral<any>).value as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodLazy:
      return getDefaults(
        (schema as unknown as z.ZodLazy<any>).schema,
      ) as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodEnum:
      return (schema as unknown as z.ZodEnum<any>).options[0] as z.infer<T>;
    case z.ZodFirstPartyTypeKind.ZodIntersection:
      const {left, right} = (
        schema as unknown as z.ZodIntersection<z.ZodTypeAny, z.ZodTypeAny>
      )._def;
      return {
        ...getDefaults(left),
        ...getDefaults(right),
      } as z.infer<T>;
    default:
      return null as z.infer<T>;
  }
};
